// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldClickable } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import { getProp } from "@insite/mobius/utilities";
import { FormFieldIcon } from "@rpsc/MobiusOverrides/FormField";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { xBaseTheme } from "@rpsc/XDL/types";
import { color, ellipsis } from "@rpsc/XDL/Utilities";
import { css } from "styled-components";

export default {
    defaultProps: {
        border: "rounded",
        sizeVariant: "default",
        mergeCss: true,
        labelProps: {
            mergeCss: true,
            size: 12,
            weight: fontConfig.weight.medium,
            color: "common.black",
            css: css`
                letter-spacing: 0;
                line-height: 17px;
                min-height: initial;
                position: relative;
                .mandatory {
                    color: ${color("danger")};
                    margin-left: 0px;
                }
            `,
        },
        errorProps: {
            mergeCss: true,
            size: 14,
            weight: "normal",
            color: "danger",
            css: css`
                letter-spacing: 0;
                line-height: 19px;
            `,
        },
        hintProps: {
            mergeCss: true,
            size: 14,
            weight: "normal",
            color: "common.text",
            css: css`
                letter-spacing: 0;
                line-height: 19px;
            `,
        },
        backgroundColor: "common.white",
        cssOverrides: {
            formInputWrapper: css`
                input,
                textarea {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        box-shadow: inset 0 1px 0 0 ${color("common.black", 0.1)};
                    }
                }
                input,
                textarea,
                select {
                    &::placeholder {
                        font-size: 14px;
                        line-height: 19px;
                        font-style: italic;
                        max-width: 100%;
                        color: ${color("common.ui4")};
                    }
                    &:not(:disabled):not(&--disabled) {
                        &:hover {
                            border: 1px solid ${color("common.ui8")};
                        }
                        &:focus-within {
                            border: 1px solid ${color("common.black")};
                        }
                    }
                    &:not(.react-datetime-picker__inputGroup__input):not([name="datetime"]):not(select) {
                        transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
                        &,
                        &:focus {
                            &:has(+ ${IconWrapper}),
                            &:has(+ ${FormFieldClickable}) {
                                ${({ _iconPosition = "right" }: any) => {
                                    return `
                                    ${_iconPosition === "right" ? `padding-right: 30px;` : "padding-left: 30px;"}
                                    `;
                                }}
                            }
                        }
                        &:disabled {
                            + ${IconWrapper} {
                                background-color: transparent;
                            }
                        }
                    }
                    &:disabled::placeholder {
                        color: ${color("common.ui6")};
                    }
                }

                button ${FormFieldIcon} svg {
                    width: 16px;
                    height: 16px;
                }

                ${FormFieldClickable}:not(:disabled) {
                    ${FormFieldIcon} {
                        color: ${color("text.link")};
                    }
                    &:hover {
                        ${FormFieldIcon} {
                            color: ${color("common.ui8")};
                        }
                    }
                }

                input,
                select {
                    &:not(.react-datetime-picker__inputGroup__input):not([name="datetime"]) {
                        ${ellipsis(1)}
                        text-overflow: ellipsis;
                    }
                }
            `,
            descriptionWrapper: css`
                &&&& {
                    margin-top: 8px;
                }
            `,
        },
    },
} as xBaseTheme["formField"];
